import React, { createContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { changePendoLanguage } from '../../utils/pendoUtils';

interface LanguageProviderValues {
  getLocale: () => string,
  getHyphenatedLocale: (locale: string) => string,
  setLocale: (locale: string, updateUrl?: boolean) => void
}

interface LanguageProviderProps {
  children: React.ReactNode
}
const LanguageContext = createContext<LanguageProviderValues>({
  getLocale: () => { throw new Error('getLocale not implemented. Did you wrap your component with LanguageProvider?'); },
  getHyphenatedLocale: () => { throw new Error('Not implemented. Did you wrap your component with LanguageProvider?'); },
  setLocale: () => { throw new Error('setLocale not implemented. Did you wrap your component with LanguageProvider?'); },
});

const LOCAL_STORAGE_KEY = 'PiLang';
// look to various places for locale/lang
// pecking order: 1. lang parameter, 2. local storage, 3. UI default from airdrop
const getInitialLocale = () => {
  // back-end sets airdrop based on lang param and also filters to only allowable values
  // via getPiLanguageSupportedForPiuiTranslations(). it defaults to en_US if value is null or unsupported.
  // we only want to use the value if it's in the URL and validated by the back-end.
  const searchParams = new URLSearchParams(window.location.search);
  const airDropLocale = window.appAirDrop.config.locale;
  let locale = searchParams.get('lang') && airDropLocale;

  if (!locale) {
    locale = window.localStorage.getItem(LOCAL_STORAGE_KEY) || airDropLocale || 'en_US';
  }
  return locale;
};

/**
 * Provides one source of truth for deriving the current language.
 */
function LanguageProvider({ children }: LanguageProviderProps) {
  const { i18n } = useTranslation();
  const [language, setLanguage] = useState(getInitialLocale);

  const getLocale = () => language;

  const getHyphenatedLocale: any = (locale: string) => locale?.replace('_', '-');

  const getOneTrustLocale = (locale: string) => {
    let onetrustLocale = getHyphenatedLocale(locale);
    if (['es-LA', 'es-AR', 'es-CL', 'es-CO', 'es-PR'].indexOf(onetrustLocale) !== -1) {
      onetrustLocale = 'es-MX';
    } else if (onetrustLocale === 'en-US') {
      onetrustLocale = 'en';
    } else if (onetrustLocale === 'fr') {
      onetrustLocale = 'fr-FR';
    }
    return onetrustLocale;
  };

  const setLocale = (locale: string, updateUrl?: boolean) => {
    // write locale to local storage. use 'piLang' as is done in session js
    window.localStorage.setItem(LOCAL_STORAGE_KEY, locale);
    const searchParams = new URLSearchParams(window.location.search);

    // change the OneTrust language if it is loaded.
    const onetrustLocale = getOneTrustLocale(locale);
    if (window.OneTrust && window.OneTrust.changeLanguage) {
      window.OneTrust.changeLanguage(onetrustLocale);
    }

    // update language for pendo help guides
    if (window.pendo) {
      changePendoLanguage(locale);
    }

    // update query param in url (see URLSearchParams interface). Note: this creates a new history entry
    // and does not preserve the state in the entry.
    if (updateUrl) {
      searchParams.set('lang', locale);

      const url: any = window.location;
      const newUrl = new URL(url);
      newUrl.search = searchParams.toString();
      window.history.pushState({}, '', newUrl);
    }

    // set html lang attribute
    document.documentElement.setAttribute('lang', getHyphenatedLocale(locale));

    // update all strings
    setLanguage(locale);
    i18n.changeLanguage(locale);
  };

  // initialization
  useEffect(() => {
    const initialLanguageFound = getInitialLocale();
    setLocale(initialLanguageFound);
  }, []);

  useEffect(() => {
    document.body.dir = i18n.dir();
  }, [i18n, i18n.language]);

  const localeFunctions = useMemo(() => ({ getLocale, setLocale, getHyphenatedLocale }), [language]);
  return (
    <LanguageContext.Provider value={localeFunctions}>
      {children}
    </LanguageContext.Provider>
  );
}

export { LanguageProvider, LanguageContext };
